import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { searchWorkspace } from '../../api/search';
import {
  EmptyState,
  PageHeader,
  PageLoading,
  StatusBanner,
  inputClassName,
  panelClassName,
  primaryButtonClassName,
  secondaryButtonClassName,
  tableCellClassName,
} from '../../components/ui';
import { CopyableValue } from '../../components/ui/CopyableValue';

function lineType(status) {
  const value = String(status || '').trim();
  if (!value || value === 'procurement_required') return 'Amazon';
  return value;
}

function productKey(line) {
  const sku = String(line?.orderItem?.sku || line?.sku || '')
    .trim()
    .toUpperCase();
  if (sku) return `sku:${sku}`;
  const asin = String(line?.orderItem?.asinIn || line?.orderItem?.asinCom || line?.asin || '')
    .trim()
    .toUpperCase();
  if (asin) return `asin:${asin}`;
  return `line:${line?.id || 'x'}`;
}

function productTitle(line) {
  return (
    line?.orderItem?.title ||
    line?.title ||
    line?.orderItem?.sku ||
    line?.sku ||
    'Product'
  );
}

function productQty(line) {
  const ordered = Number(line?.orderItem?.quantityOrdered || line?.quantity || 1);
  const extra = Number(line?.extraQuantity || 0);
  return (Number.isFinite(ordered) ? ordered : 1) + (Number.isFinite(extra) ? extra : 0);
}

function groupLinesByProduct(lines) {
  const order = [];
  const map = new Map();
  for (const line of lines || []) {
    const key = productKey(line);
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key).push(line);
  }
  return order.map((key) => {
    const groupLines = map.get(key) || [];
    return {
      key,
      lines: groupLines,
      totalQty: groupLines.reduce((sum, line) => sum + productQty(line), 0),
      title: productTitle(groupLines[0]),
      sku: groupLines[0]?.orderItem?.sku || groupLines[0]?.sku || '',
      asin:
        groupLines[0]?.orderItem?.asinIn ||
        groupLines[0]?.orderItem?.asinCom ||
        groupLines[0]?.asin ||
        '',
      imageUrl: groupLines[0]?.orderItem?.imageUrl || '',
    };
  });
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('en-IN');
}

function formatDay(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString('en-IN');
}

function SearchPage({ token }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [input, setInput] = useState(() => searchParams.get('q') || '');
  const [query, setQuery] = useState(() => String(searchParams.get('q') || '').trim());
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState({ type: 'ready', message: '' });
  const [loading, setLoading] = useState(false);
  const [closedBatches, setClosedBatches] = useState(() => new Set());
  const [collapsedProducts, setCollapsedProducts] = useState(() => new Set());

  useEffect(() => {
    const next = String(query || '').trim();
    if (next.length < 2) {
      setResult(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setStatus({ type: 'ready', message: '' });

    searchWorkspace(token, next)
      .then((payload) => {
        if (cancelled) return;
        setResult(payload);
        setClosedBatches(new Set());
        setCollapsedProducts(new Set());
        if (!payload.batches?.length) {
          setStatus({ type: 'error', message: 'No orders found for this search.' });
        }
      })
      .catch((error) => {
        if (cancelled) return;
        setResult(null);
        setStatus({ type: 'error', message: error.message });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, query]);

  const runSearch = (event) => {
    event?.preventDefault?.();
    const next = String(input || '').trim();
    if (next.length < 2) {
      setStatus({ type: 'error', message: 'Enter at least 2 characters.' });
      return;
    }
    setQuery(next);
    setSearchParams(next ? { q: next } : {});
  };

  const toggleBatch = (batchId) => {
    setClosedBatches((current) => {
      const next = new Set(current);
      if (next.has(batchId)) next.delete(batchId);
      else next.add(batchId);
      return next;
    });
  };

  const toggleProduct = (key) => {
    setCollapsedProducts((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const batches = result?.batches || [];
  const summary = useMemo(() => {
    if (!result?.batches?.length) return '';
    const labels = [...new Set(result.batches.flatMap((batch) => batch.matchLabels || []))];
    return [
      `${result.total} batch${result.total === 1 ? '' : 'es'}`,
      labels.length ? `matched on ${labels.join(', ')}` : null,
      result.truncated ? `showing first ${result.batches.length}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  }, [result]);

  return (
    <section className="grid gap-4">
      <PageHeader
        title="Search"
        subtitle="Find a HAWB, order ID, US order, tracking ID, SKU, or batch — then open the full procurement batch."
      />

      <StatusBanner status={status} setStatus={setStatus} />

      <article className="rounded-[10px] border border-slate-200 bg-white p-4 shadow-sm">
        <form className="flex flex-wrap gap-2" onSubmit={runSearch}>
          <input
            className={`${inputClassName} min-w-[16rem] flex-1 font-mono`}
            type="search"
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="HAWB, order ID, US order, tracking, batch name…"
            autoComplete="off"
            spellCheck={false}
            autoFocus
          />
          <button type="submit" className={primaryButtonClassName} disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </form>
        {summary ? <p className="mt-3 text-sm text-slate-600">{summary}</p> : null}
      </article>

      {loading && !batches.length ? <PageLoading label="Searching batches…" /> : null}

      {!loading && query && !batches.length ? (
        <EmptyState
          title="No matching batches"
          description="Try a HAWB, Amazon order ID, US order ID, tracking ID, SKU, or batch ID (BH0001)."
        />
      ) : null}

      {batches.map((batch) => {
        const closed = closedBatches.has(batch.id);
        const matched = new Set((batch.matchedLineIds || []).map((id) => String(id)));
        // Only show lines that actually matched the query (not every line in the batch).
        // If the hit was batch-level with no line ids, fall back to all lines.
        const displayLines =
          matched.size > 0
            ? (batch.lines || []).filter((line) => matched.has(String(line.id)))
            : batch.lines || [];
        const groups = groupLinesByProduct(displayLines);
        return (
          <article
            key={batch.id}
            className={`${panelClassName} w-full overflow-hidden`}
          >
            <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-200/90 bg-white px-5 py-3.5">
              <div className="min-w-0">
                <h3 className="flex flex-wrap items-center gap-2 text-[15px] font-semibold tracking-tight text-slate-900">
                  <span className="truncate font-mono tracking-wide">
                    {batch.batchCode || batch.name || `Batch ${String(batch.id).slice(0, 8)}`}
                  </span>
                  {batch.batchCode && batch.name && batch.name !== batch.batchCode ? (
                    <span className="truncate text-sm font-medium text-slate-500">{batch.name}</span>
                  ) : null}
                  <span className="rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-semibold capitalize text-slate-700 ring-1 ring-inset ring-slate-200">
                    {batch.status}
                  </span>
                  {(batch.matchLabels || []).map((label) => (
                    <span
                      key={label}
                      className="rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-800 ring-1 ring-inset ring-amber-200"
                    >
                      {label}
                    </span>
                  ))}
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  {displayLines.length} matching line{displayLines.length === 1 ? '' : 's'}
                  {(batch.lines || []).length > displayLines.length
                    ? ` · ${(batch.lines || []).length} in batch`
                    : null}
                  {' · '}
                  Created {formatWhen(batch.createdAt)}
                  {batch.updatedAt && batch.updatedAt !== batch.createdAt ? (
                    <>
                      {' · '}
                      Last edit {formatWhen(batch.updatedAt)}
                    </>
                  ) : null}
                  {' · '}
                  <span className="font-medium text-slate-600">{batch.creatorName || 'System'}</span>
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Link
                  to={`/admin/procurement?batch=${encodeURIComponent(batch.id)}`}
                  className={secondaryButtonClassName}
                >
                  Open in Procurement
                </Link>
                <button
                  type="button"
                  className={secondaryButtonClassName}
                  onClick={() => toggleBatch(batch.id)}
                >
                  {closed ? 'Show batch' : 'Hide batch'}
                </button>
              </div>
            </div>

            {closed ? null : displayLines.length === 0 ? (
              <EmptyState
                title="No matching lines in this batch"
                description="Open the batch in Procurement to see the full list."
              />
            ) : (
              <div className="divide-y divide-slate-100">
                {groups.map((group) => {
                  const groupCollapseKey = `${batch.id}:${group.key}`;
                  const hidden = collapsedProducts.has(groupCollapseKey);
                  return (
                    <div key={groupCollapseKey} className="bg-white">
                      <div className="flex flex-wrap items-center gap-3 bg-slate-50 px-5 py-3">
                        {group.imageUrl ? (
                          <img
                            src={group.imageUrl}
                            alt=""
                            className="h-10 w-10 rounded-md object-cover ring-1 ring-slate-200"
                          />
                        ) : (
                          <span className="grid h-10 w-10 place-items-center rounded-md bg-slate-200 text-[10px] font-semibold text-slate-500">
                            SKU
                          </span>
                        )}
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-semibold text-slate-900">{group.title}</p>
                          <p className="font-mono text-[11px] text-slate-500">
                            {[
                              group.sku ? `SKU ${group.sku}` : null,
                              group.asin ? `ASIN ${group.asin}` : null,
                              `${group.totalQty} qty`,
                              `${group.lines.length} order${group.lines.length === 1 ? '' : 's'}`,
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </p>
                        </div>
                        <button
                          type="button"
                          className="text-sm font-semibold text-slate-600 hover:text-slate-900"
                          onClick={() => toggleProduct(groupCollapseKey)}
                        >
                          {hidden ? '▸ Show orders' : '▾ Hide orders'}
                        </button>
                      </div>

                      {hidden ? null : (
                        <div className="overflow-x-auto">
                          <table className="w-full min-w-[64rem] border-collapse">
                            <thead>
                              <tr className="bg-white">
                                {[
                                  'Order date',
                                  'Ship by',
                                  'Order ID',
                                  'Customer',
                                  'US order',
                                  'US tracking',
                                  'Dummy tracking',
                                  'HAWB',
                                  'Type',
                                  'Status',
                                ].map((head) => (
                                  <th
                                    key={head}
                                    className={`${tableCellClassName} text-[11px] font-semibold uppercase tracking-wide text-slate-500`}
                                  >
                                    {head}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {group.lines.map((line) => {
                                const order = line.orderItem?.order || {};
                                return (
                                  <tr key={line.id} className="bg-amber-50/70">
                                    <td className={tableCellClassName}>{formatDay(order.orderDate)}</td>
                                    <td className={tableCellClassName}>{formatDay(order.shipByDate)}</td>
                                    <td className={tableCellClassName}>
                                      {order.amazonOrderId ? (
                                        <CopyableValue
                                          value={order.amazonOrderId}
                                          className="font-mono text-xs font-semibold"
                                        />
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className={tableCellClassName}>{order.buyerName || '—'}</td>
                                    <td className={tableCellClassName}>
                                      {line.usOrderId ? (
                                        <CopyableValue
                                          value={line.usOrderId}
                                          className="font-mono text-xs"
                                        />
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className={tableCellClassName}>
                                      {line.usTrackingId ? (
                                        <CopyableValue
                                          value={line.usTrackingId}
                                          className="font-mono text-xs"
                                        />
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className={tableCellClassName}>
                                      {order.trackingDummy ? (
                                        <CopyableValue
                                          value={order.trackingDummy}
                                          className="font-mono text-xs"
                                        />
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className={tableCellClassName}>
                                      {line.hawb ? (
                                        <CopyableValue value={line.hawb} className="font-mono text-xs" />
                                      ) : (
                                        '—'
                                      )}
                                    </td>
                                    <td className={tableCellClassName}>{lineType(line.inventoryStatus)}</td>
                                    <td className={tableCellClassName}>
                                      {line.confirmed ? 'Confirmed' : order.status || '—'}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </article>
        );
      })}
    </section>
  );
}

export default SearchPage;
